"use client"

import * as React from "react"
import { Link } from "react-router-dom"
import { MailIcon, PhoneIcon, UserIcon } from "lucide-react"

import { Button, IdentityPreview, cn } from "@ibada/ui"
import { AliveAvatar } from "@/shared/components/alive-cells"
import { ProgressRowPill, ProgressRowPillTone } from "@/shared/components/progress-rows"
import { MemberStatus } from "@/features/members/model/members-types"

import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth"
import { useAuthContextStore } from "@/features/auth/services/auth-context-store"
import { useTranslations } from "@/i18n"
import { useUser } from "@/shims/clerk-react"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"
import { useIdentityPreview } from "@/shared/hooks/use-identity-preview"
import { identityRefIsResolvable, type IdentityRef } from "@/shared/lib/api/identity-api"

type PersonPreviewFallback = {
  /** Name already on hand at the call site — shown instantly, before/without the enrichment fetch. */
  name: string
  avatarUrl?: string | null
  /** Context line the caller knows best (role, designation, …). */
  subtitle?: React.ReactNode
}

type CommonProps = {
  /** Any unique handle. The backend maps member <-> workspace user (by id or unique email) to fill gaps. */
  identity: IdentityRef
  fallback: PersonPreviewFallback
  badges?: React.ReactNode
  primaryAction?: React.ReactNode
}

export type PersonPreviewProps =
  | (CommonProps & {
      variant: "popover" | "hover"
      /** The trigger (a name link, avatar, chip). */
      children: React.ReactNode
    })
  | (CommonProps & {
      variant: "dialog"
      open: boolean
      onOpenChange: (open: boolean) => void
      ariaTitle?: string
    })

function useOptionalClerkUserProfile() {
  try {
    const { isLoaded, isSignedIn, user } = useUser()
    if (!isLoaded || !isSignedIn || !user) return null

    const name =
      [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
      user.fullName ||
      user.primaryEmailAddress?.emailAddress ||
      null

    return {
      name,
      avatarUrl: user.imageUrl || null,
    }
  } catch {
    // Some unit tests and package-level renderers exercise this shared cell outside ClerkProvider. The cell
    // still has enough local fallback data to render; the live Clerk override is only an in-app freshness boost.
    return null
  }
}

/**
 * Drop-in person/identity card. Wrap a name/avatar/chip with the popover or hover variant, or drive
 * the dialog variant from an open state. It shows the caller's `fallback` immediately and lazily
 * fetches the merged identity (only once the card opens), enriching name/avatar/email from whichever
 * of the member/user records has it. See [[useIdentityPreview]].
 */
export function PersonPreview(props: PersonPreviewProps) {
  const { identity, fallback, badges, primaryAction } = props
  const [internalOpen, setInternalOpen] = React.useState(false)
  const t = useTranslations("common")
  const tMembers = useTranslations("people.members")
  const branchSlug = useActiveBranchSlug()
  const workspaceSlug = useActiveWorkspaceSlug()

  const open = props.variant === "dialog" ? props.open : internalOpen
  const { data } = useIdentityPreview(identity, { enabled: open })

  const name = data?.name || fallback.name
  const avatarSrc = data?.avatarUrl ?? fallback.avatarUrl ?? null
  const email = data?.email ?? data?.account?.loginEmail ?? null
  const subtitleMeta = email ? (
    <>
      <MailIcon />
      <span className='truncate'>{email}</span>
    </>
  ) : undefined

  // The member id may be known up-front or only surface after the member<->user enrichment lands.
  const memberId = data?.memberId ?? identity.memberId ?? null
  const viewProfileAction = memberId ? (
    <Button asChild variant='default' size='sm' className='rounded-full'>
      <Link to={buildBranchAdminUrl(branchSlug, `/members/${memberId}`, workspaceSlug)}>
        <UserIcon aria-hidden data-icon='inline-start' />
        {t("personCard.viewRecord")}
      </Link>
    </Button>
  ) : undefined
  // Calling is offered only when the directory actually has a phone number.
  const phone = data?.phone?.trim() || null
  const callAction = phone ? (
    <Button asChild variant='outline' size='sm' className='rounded-full'>
      <a href={`tel:${phone.replace(/\s+/g, "")}`}>
        <PhoneIcon aria-hidden data-icon='inline-start' />
        {t("personCard.call")}
      </a>
    </Button>
  ) : undefined

  // Without caller pills, show the member's directory status (green only when active).
  const memberStatus = Object.values(MemberStatus).find((status) => status === data?.memberStatus)
  const statusPill =
    !badges && memberStatus ? (
      <ProgressRowPill tone={memberStatus === MemberStatus.Active ? ProgressRowPillTone.Success : ProgressRowPillTone.Neutral}>
        {tMembers(`statuses.${memberStatus}`)}
      </ProgressRowPill>
    ) : undefined

  const cardProps = {
    layout: "detail" as const,
    // Initials + tint follow the name the trigger showed, so the card's avatar matches the list avatar.
    avatar: { name: fallback.name || name, src: avatarSrc },
    title: name,
    subtitle: fallback.subtitle ?? undefined,
    subtitleMeta,
    badges: badges ?? statusPill,
    primaryAction: primaryAction ?? viewProfileAction,
    secondaryAction: callAction,
  }

  if (props.variant === "dialog") {
    return (
      <IdentityPreview
        variant='dialog'
        open={props.open}
        onOpenChange={props.onOpenChange}
        ariaTitle={props.ariaTitle ?? name}
        {...cardProps}
      />
    )
  }

  return (
    <IdentityPreview variant={props.variant} open={internalOpen} onOpenChange={setInternalOpen} {...cardProps}>
      {props.children}
    </IdentityPreview>
  )
}

/**
 * A ready-made person table/list cell: a small avatar + name that visibly reads as a profile (per the
 * "always show an avatar next to a person" rule) AND is the trigger for the hover/popover card. Falls
 * back to plain text when there's no name, and to a static (non-interactive) chip when the identity
 * can't be resolved (no id/email), so it never offers an empty preview.
 */
export function PersonPreviewCell({
  identity,
  name,
  avatarUrl,
  subtitle,
  variant = "hover",
  emptyLabel = "-",
  enablePreview = true,
  useCurrentUserFallback = true,
}: {
  identity: IdentityRef
  name: string | null | undefined
  avatarUrl?: string | null
  subtitle?: React.ReactNode
  variant?: "hover" | "popover"
  emptyLabel?: React.ReactNode
  /** Disable hover/popover enrichment when the caller only needs the shared avatar/name presentation. */
  enablePreview?: boolean
  /** Use the current authenticated user as a freshness fallback when this cell is rendering that same user. */
  useCurrentUserFallback?: boolean
}) {
  const authMe = useAuthContextStore((state) => state.authMe)
  const clerkProfile = useOptionalClerkUserProfile()

  const isCurrentUser =
    useCurrentUserFallback &&
    Boolean(identity.userId) &&
    (identity.userId === authMe?.auth.localUserId ||
      identity.userId === authMe?.user.id ||
      identity.userId === authMe?.user.clerkUserId)

  const storedUser = authMe?.user ?? null
  const storedName = storedUser ? `${storedUser.firstName} ${storedUser.lastName}`.trim() || storedUser.email : null
  const resolvedName = isCurrentUser ? clerkProfile?.name || storedName || name : name
  const resolvedAvatarUrl = isCurrentUser ? clerkProfile?.avatarUrl ?? storedUser?.avatarUrl ?? avatarUrl : avatarUrl

  if (!resolvedName) return <>{emptyLabel}</>

  const interactive = enablePreview && identityRefIsResolvable(identity)
  const inner = (
    <>
      <AliveAvatar small name={resolvedName} src={resolvedAvatarUrl} />
      <span
        className={cn(
          "truncate font-semibold",
          // Only the name reads as a link (dotted underline), never the avatar's initials.
          interactive && "underline decoration-muted-foreground/60 decoration-dotted underline-offset-4 group-hover/person:decoration-solid",
        )}
      >
        {resolvedName}
      </span>
    </>
  )

  if (!interactive) {
    return <span className='inline-flex min-w-0 items-center gap-2'>{inner}</span>
  }

  return (
    <PersonPreview variant={variant} identity={identity} fallback={{ name: resolvedName, avatarUrl: resolvedAvatarUrl, subtitle }}>
      <button
        type='button'
        className='group/person inline-flex min-w-0 items-center gap-2 rounded-md text-left focus-visible:outline-2 focus-visible:outline-offset-2'
      >
        {inner}
      </button>
    </PersonPreview>
  )
}
